import { useState } from "react";
import { useAuth } from "@/lib/auth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { AlertTriangle, Loader2 } from "lucide-react";

interface CancelMeetingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  meetingId: string;
  otherPersonName: string;
  scheduledAt: string | null;
  onCancelled: () => void;
}

export function CancelMeetingDialog({
  open,
  onOpenChange,
  meetingId,
  otherPersonName,
  scheduledAt,
  onCancelled,
}: CancelMeetingDialogProps) {
  const { user } = useAuth();
  const [reason, setReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isValidReason = reason.trim().length >= 20;

  const handleCancel = async () => {
    if (!user || !isValidReason) return;

    setIsSubmitting(true);

    try {
      // Update mentorship_meetings with cancellation
      const { error: updateError } = await supabase
        .from("mentorship_meetings")
        .update({
          status: "cancelled",
          cancelled_at: new Date().toISOString(),
          cancelled_by: user.id,
          cancellation_reason: reason.trim(),
        })
        .eq("id", meetingId);

      if (updateError) throw updateError;

      toast.success("Meeting cancelled successfully.");

      setReason("");
      onOpenChange(false);
      onCancelled();
    } catch (error: any) {
      console.error("Error cancelling meeting:", error);
      toast.error("Failed to cancel meeting. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            Cancel Scheduled Meeting
          </DialogTitle>
          <DialogDescription>
            You are about to cancel your meeting with <strong>{otherPersonName}</strong>.
            {scheduledAt && (
              <span className="block mt-1">
                Scheduled for:{" "}
                {new Date(scheduledAt).toLocaleString("en-US", {
                  weekday: "short",
                  month: "short",
                  day: "numeric",
                  hour: "numeric",
                  minute: "2-digit",
                })}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="reason">
              Please explain why you need to cancel{" "}
              <span className="text-muted-foreground">(minimum 20 characters)</span>
            </Label>
            <Textarea
              id="reason"
              placeholder="I need to cancel because..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
              className="resize-none"
            />
            <p className="text-xs text-muted-foreground">
              {reason.trim().length}/20 characters
              {!isValidReason && reason.length > 0 && (
                <span className="text-destructive ml-2">
                  ({20 - reason.trim().length} more needed)
                </span>
              )}
            </p>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Keep Meeting
          </Button>
          <Button
            variant="destructive"
            onClick={handleCancel}
            disabled={!isValidReason || isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Cancelling...
              </>
            ) : (
              "Cancel Meeting"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
